import { Box } from '@mui/material';
import { FC, useCallback, useState } from 'react';

import AmountIcon from '../../../../assets/icons/amount.png';
import CalendarIcon from '../../../../assets/icons/calendar.png';
import InfoIcon from '../../../../assets/icons/info.png';
import { ReactComponent as ProjectContextInfoIcon } from '../../../../assets/icons/information.svg';
import { UserSelectors } from '../../../../reducers/user';
import { WorkSelectors } from '../../../../reducers/work';
import { InformationTooltip } from '../../../../shared';
import { ContributorReviewSteps, ContributorWorkSteps } from '../../../../types/contributorWork';
import { Attachment, TaskContext } from '../../../../types/work';
import ProjectContextDialog from '../projectContext';
import * as S from './styled';

const tooltipText =
    'This work requires skills that aren\'t\n listed in your profile.\n\
    We show the work anyway in case you want to give it a try. You can easily adjust your profile settings if you prefer to work exclusively within your skill set.';

const parseContextTooltip = (c: TaskContext) => {
    return `File: ${c.file} \nEntity: ${c.entity}\n\n Potential Use: ${c.potentialUse}`;
};

type PrimaryVariantProps = {
    type: string;
    workType: string;
    skillsNeeded?: string[];
    context?: TaskContext[];
    projectName: string;
    projectDescription: string;
    projectAttachments?: Attachment[];
    isSeniorWork?: boolean;
    onAccept: () => void;
    onSkip: () => void;
};

const PrimaryVariant: FC<PrimaryVariantProps> = ({
    workType,
    skillsNeeded,
    context,
    onAccept,
    onSkip
}) => {
    const { workLoading } = WorkSelectors();
    const { profile } = UserSelectors();
    const showSkillsTooltip = profile && skillsNeeded && !skillsNeeded.every(s => profile.skills.includes(s));

    return (
        <>
            <S.WorkTypeTitle>Work type</S.WorkTypeTitle>
            <S.WorkTypeContent>{workType}</S.WorkTypeContent>
            {/* <S.WrongWorkTypeLink onClick={onClickWrongWorkType}>
            Wrong work type?
        </S.WrongWorkTypeLink> */}
            <Box height={24} />
            <S.DividerLine />
            <S.SkillsNeededText>Skills needed</S.SkillsNeededText>
            <S.SkillsBox>
                {skillsNeeded?.map((skill, index) => (
                    <S.SkillBox key={index}>
                        <S.SkillNameText>{skill}</S.SkillNameText>
                    </S.SkillBox>
                ))}
                {showSkillsTooltip && 
                    <InformationTooltip text={tooltipText}>
                        <img src={InfoIcon} />
                    </InformationTooltip>
                }
            </S.SkillsBox>
            <Box height={24}/>
            <S.DividerLine />
            {
                (context && context.length > 0) &&
                <>
                    <S.ProjectText>Context</S.ProjectText>
                    <S.WorkContext>
                        {context && context.length > 0 &&
                            'Relevant entities for this task (hover for more info)'
                        }
                    </S.WorkContext>
                        
                    <S.ContextBox maxHeight={160}>
                        {context && context.length > 0 && context.map((c, index) => (
                            <InformationTooltip text={parseContextTooltip(c)} key={index}>
                                <S.SkillBox>
                                    <S.SkillNameText>{c.entity}</S.SkillNameText>
                                </S.SkillBox>
                            </InformationTooltip>
                                
                        ))}
                    </S.ContextBox>
                    <Box height={24} />
                    <S.DividerLine />
                    <Box height={24} />
                </>
            }
            
            <S.Spacer />
            <S.ActionsBox>
                <S.AcceptButton disabled={workLoading} onClick={() => !workLoading && onAccept()}>Accept</S.AcceptButton>
                <S.SkipButton disabled={workLoading} onClick={() => !workLoading && onSkip()}>Skip</S.SkipButton>
            </S.ActionsBox>
        </>
    );
};

type SecondaryVariantProps = {
    type: string;
    context?: TaskContext[];
    projectName: string;
    projectDescription: string;
    projectAttachments?: Attachment[];
    isSeniorWork?: boolean;
};

const SecondaryVariant: FC<SecondaryVariantProps> = ({
    context
}) => {
    const onClickCodingGitubGuidelines = useCallback(() => {
        window.open('https://docs.caas.ai/community/general', '_blank');
    }, []);
    
    return (
        <>
            {
                (context && context.length > 0) && 
                <>
                    <S.ProjectText>Context</S.ProjectText>
                    <S.WorkContext>
                        Relevant entities for this task (hover for more info)
                    </S.WorkContext>
                        
                    <S.ContextBox maxHeight={85}>
                        {context && context.length > 0 && context.map((c, index) => (
                            <InformationTooltip text={parseContextTooltip(c)} key={index}>
                                <S.SkillBox>
                                    <S.SkillNameText>{c.entity}</S.SkillNameText>
                                </S.SkillBox>
                            </InformationTooltip>
                        ))}
                    </S.ContextBox>
                    <Box height={24} />
                    <S.DividerLine />
                    <Box height={24} />
                </>
            }
            <S.SpecialInstructionsTitle>
                Special instructions
            </S.SpecialInstructionsTitle>
            <Box height={15} />
            <S.SpecialInstructionsText>
                Make sure you follow our{' '}
                <S.CodingGithubGuidelinesLink
                    onClick={onClickCodingGitubGuidelines}
                >
                    coding and GitHub guidelines
                </S.CodingGithubGuidelinesLink>
                , otherwise your work will be rejected.
            </S.SpecialInstructionsText>
            <Box height={15} />
            <S.SpecialInstructionsText>
                In particular, only use your designated branch, do not merge
                code yourself, and always do PR changes requests on the same
                branch.
            </S.SpecialInstructionsText>
        </>
    );
};

type TertiaryVariantProps = {
    type: string;
    text: string;
};

const TertiaryVariant: FC<TertiaryVariantProps> = ({ text }) => (
    <>
        <Box height={20} />
        <S.TertiaryVariantText>{text}</S.TertiaryVariantText>
    </>
);

export type TermsProps = {
    datetime: string;
    amount: string | null;
    variant: PrimaryVariantProps | SecondaryVariantProps | TertiaryVariantProps;
    currentStep: ContributorWorkSteps | ContributorReviewSteps;
};

const Terms: FC<TermsProps> = ({ datetime, amount, variant, currentStep }) => {
    const [ projectContextDialogOpen, setProjectContextDialogOpen ] = useState(false);
    
    const handleOpenProjectContextDialog = () => {
        setProjectContextDialogOpen(true);
    };
    
    const handleCloseProjectContextDialog = () => {
        setProjectContextDialogOpen(false);
    };
    
    // Extract project context props from variant
    const projectName = (variant as PrimaryVariantProps | SecondaryVariantProps).projectName;
    const projectDescription = (variant as PrimaryVariantProps | SecondaryVariantProps).projectDescription;
    const projectAttachments = (variant as PrimaryVariantProps | SecondaryVariantProps).projectAttachments;
    const isSeniorWork = (variant as PrimaryVariantProps | SecondaryVariantProps).isSeniorWork;

    const Variant = useCallback(() => {
        switch (variant.type) {
            case 'Primary':
                return <PrimaryVariant {...(variant as PrimaryVariantProps)} />;
            case 'Secondary':
                return (
                    <SecondaryVariant {...(variant as SecondaryVariantProps)} />
                );
            case 'Tertiary':
                return (
                    <TertiaryVariant {...(variant as TertiaryVariantProps)} />
                );
            default:
                return <></>;
        }
    }, [ variant ]);
    return (
        <S.Container height={currentStep === ContributorWorkSteps.SubmitPullRequest ? '243px' : '100%'}>
            {isSeniorWork && projectDescription && (
                <>
                    <S.ProjectContextBox>
                        <S.ProjectContextTitle>
                            Project Context
                            <S.ProjectContextIcon onClick={handleOpenProjectContextDialog}>
                                <ProjectContextInfoIcon />
                            </S.ProjectContextIcon>
                        </S.ProjectContextTitle>
                    </S.ProjectContextBox>
                    <Box height={24} />
                    <S.DividerLine />
                </>
            )}
            <S.TermsText>Terms</S.TermsText>
            <S.CalendarBox>
                <img src={CalendarIcon} />
                <S.CalenderText>{datetime}</S.CalenderText>
            </S.CalendarBox>
            {amount &&
                <S.AmountBox>
                    <img src={AmountIcon} />
                    <S.AmountText>{amount}</S.AmountText>
                </S.AmountBox>
            }
            <Box height={24} />
            <S.DividerLine />
            <Variant />
            
            {/* Project Context Dialog */}
            {isSeniorWork && projectDescription && (
                <ProjectContextDialog
                    isOpen={projectContextDialogOpen}
                    onClose={handleCloseProjectContextDialog}
                    projectName={projectName}
                    projectDescription={projectDescription}
                    projectAttachments={projectAttachments}
                />
            )}
        </S.Container>
    );
};

export default Terms;
